import React, { useMemo, useState } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useDrivers } from '../hooks/useDrivers';
import { useDriverMutations } from '../hooks/useDriverMutations';
import { useProvisionDriver, type EmploymentType, type ProvisionDriverResult } from '../hooks/useProvisionDriver';
import { useDriverAdmin } from '../hooks/useDriverAdmin';
import { useVehicles } from '../hooks/useVehicles';
import { useCompanies } from '../hooks/useCompanies';
import AddDriverModal from '../components/AddDriverModal';
import DriverProfileModal from '../components/DriverProfileModal';
import type { Driver } from '../lib/types';
import {
  Search, Plus, ChevronDown, ChevronUp, Filter,
  Edit, Trash2, UserCheck, UserX,
  Star,
  CheckCircle, AlertCircle,
} from 'lucide-react';

type SortField = 'full_name' | 'email' | 'status' | 'performance_score' | 'total_trips';

function Toast({ msg, ok }: { msg: string; ok: boolean }) {
  return (
    <div className={`fixed bottom-6 right-6 z-[999] flex items-center gap-3 px-5 py-3 rounded-2xl shadow-modal text-white text-sm font-medium animate-slide-up`}
      style={{ background: ok ? 'linear-gradient(135deg,#059669,#047857)' : 'linear-gradient(135deg,#dc2626,#b91c1c)' }}>
      {ok ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
      {msg}
    </div>
  );
}

function Skeleton({ className }: { className?: string }) {
  return <div className={`skeleton rounded-xl ${className ?? ''}`} />;
}

const emptyNewDriver = {
  full_name: '',
  email: '',
  phone: '',
  driver_code: '',
  license_no: '',
  password: '',
  company_id: '',
  vehicle_id: '',
  employment_type: '' as EmploymentType | '',
  is_insured: false,
  insurance_company: '',
  insurance_policy_no: '',
  send_email: true,
};

const Drivers: React.FC = () => {
  const { companyId, isSuperAdmin, isCompanyAdmin, role } = useAuth();
  const showCompanyPicker = isSuperAdmin || role === 'operator';
  const scopedCompanyId = isCompanyAdmin ? companyId : null;
  const [sortField, setSortField] = useState<SortField>('full_name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [editMode, setEditMode] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<Driver | null>(null);

  const { drivers, total, loading, refetch } = useDrivers({
    search: searchTerm,
    status: filterStatus === 'all' ? 'all' : filterStatus as Driver['status'],
    page: currentPage,
    perPage: entriesPerPage,
    sortField,
    sortDirection: sortDir,
    companyId: scopedCompanyId,
  });

  const { updateDriver, deleteDriver } = useDriverMutations();
  const { provisionDriver } = useProvisionDriver();
  const { resetDriverPassword } = useDriverAdmin();
  const { companies } = useCompanies();
  const [addCompanyId, setAddCompanyId] = useState(companyId ?? '');
  const { vehicles: fleetVehicles } = useVehicles({
    companyId: addCompanyId || companyId || null,
  });
  const { vehicles: editVehicles } = useVehicles({
    companyId: selectedDriver?.company_id ?? companyId ?? null,
  });
  const editAvailableVehicles = useMemo(
    () => editVehicles.filter((v) => v.status === 'active'),
    [editVehicles],
  );

  const availableVehicles = useMemo(
    () => fleetVehicles.filter((v) => v.status === 'active'),
    [fleetVehicles],
  );

  // Add form state
  const [newDriver, setNewDriver] = useState({ ...emptyNewDriver, company_id: companyId ?? '' });
  const [adding, setAdding] = useState(false);
  const [provisionResult, setProvisionResult] = useState<ProvisionDriverResult | null>(null);
  // Edit form state
  const [editForm, setEditForm] = useState({ full_name: '', email: '', phone: '', driver_code: '', license_no: '', vehicle_id: '' });

  const showToast = (msg: string, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3000);
  };

  const totalPages = Math.max(1, Math.ceil(total / entriesPerPage));

  const handleSort = (field: SortField) => {
    if (sortField === field) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
    setCurrentPage(1);
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return (
      <span className="ml-1 text-slate-300 inline-flex flex-col leading-none">
        <ChevronUp className="h-2.5 w-2.5" /><ChevronDown className="h-2.5 w-2.5" />
      </span>
    );
    return <span className="ml-1 text-electric-500">{sortDir === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</span>;
  };

  const statusBadge = (status: string) => {
    const cls = { active: 'badge-active', inactive: 'badge-inactive', suspended: 'badge-suspended' }[status] ?? 'badge-inactive';
    const dot = { active: 'bg-emerald-500', inactive: 'bg-slate-400', suspended: 'bg-red-500' }[status] ?? 'bg-slate-400';
    return <span className={cls}><span className={`w-1.5 h-1.5 rounded-full inline-block mr-1.5 ${dot}`} />{status}</span>;
  };

  const handleOpenDriver = (driver: Driver) => {
    setSelectedDriver(driver);
    setEditForm({
      full_name: driver.full_name,
      email: driver.email ?? '',
      phone: driver.phone ?? '',
      driver_code: driver.driver_code ?? '',
      license_no: driver.license_no ?? '',
      vehicle_id: driver.vehicle_id ?? '',
    });
    setIsModalOpen(true);
    setActiveTab('profile');
    setEditMode(false);
  };

  const handleToggleStatus = async (driver: Driver, e: React.MouseEvent) => {
    e.stopPropagation();
    const newStatus: Driver['status'] = driver.status === 'active' ? 'inactive' : 'active';
    const { error } = await updateDriver(driver.id, { status: newStatus });
    if (error) { showToast(error, false); } else {
      showToast(`${driver.full_name} is now ${newStatus}`);
      refetch();
    }
  };

  const handleDelete = async (driver: Driver) => {
    const { error } = await deleteDriver(driver.id);
    setDeleteConfirm(null);
    if (error) { showToast(error, false); } else {
      showToast(`${driver.full_name} deleted`);
      refetch();
    }
  };

  const handleAddDriver = async () => {
    if (!newDriver.full_name.trim()) { showToast('Full name is required', false); return; }
    if (!newDriver.driver_code.trim()) { showToast('Official taxilegitimation number (förarkod) is required', false); return; }
    if (!newDriver.email.trim()) { showToast('Email is required for mobile login', false); return; }

    const targetCompanyId = newDriver.company_id || companyId;
    if (!targetCompanyId) { showToast('Company is required', false); return; }
    if (!newDriver.vehicle_id) { showToast('Vehicle is required for P-ivilotid plate lookup', false); return; }

    setAdding(true);
    const { data, error } = await provisionDriver({
      full_name: newDriver.full_name.trim(),
      driver_code: newDriver.driver_code.trim(),
      email: newDriver.email.trim(),
      phone: newDriver.phone.trim() || undefined,
      license_no: newDriver.license_no.trim() || undefined,
      company_id: targetCompanyId,
      vehicle_id: newDriver.vehicle_id,
      employment_type: newDriver.employment_type || undefined,
      is_insured: newDriver.is_insured,
      insurance_company: newDriver.insurance_company.trim() || undefined,
      insurance_policy_no: newDriver.insurance_policy_no.trim() || undefined,
      password: newDriver.password.trim() || undefined,
      send_email: newDriver.send_email,
    });
    setAdding(false);

    if (error) { showToast(error, false); return; }

    setProvisionResult(data);
    setNewDriver({ ...emptyNewDriver, company_id: companyId ?? '' });
    setAddCompanyId(companyId ?? '');
    setIsAddModalOpen(false);
    refetch();
  };

  const handleResetPassword = async (driver: Driver, sendEmail: boolean) => {
    const { data, error } = await resetDriverPassword(driver.id, sendEmail);
    if (error) { showToast(error, false); return; }
    if (data) {
      setProvisionResult(data);
      showToast(sendEmail ? 'Credentials reset — check email if configured' : 'Password reset');
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedDriver) return;
    const driverCode = editForm.driver_code.trim().replace(/\s+/g, '');
    if (!driverCode || driverCode.length < 4) {
      showToast('Official taxilegitimation number (förarkod) is required (min 4 characters)', false);
      return;
    }
    const { error } = await updateDriver(selectedDriver.id, {
      ...editForm,
      driver_code: driverCode,
      vehicle_id: editForm.vehicle_id || null,
    });
    if (error) { showToast(error, false); } else {
      showToast('Profile updated successfully');
      setEditMode(false);
      refetch();
    }
  };

  // ── Add Modal — hoisted to AddDriverModal.tsx (stable component identity) ──

  const CredentialsModal = () => {
    if (!provisionResult) return null;
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-modal w-full max-w-md animate-slide-up p-6">
          <h3 className="font-bold text-slate-800 text-lg mb-1">Driver provisioned</h3>
          <p className="text-sm text-slate-500 mb-4">Share these credentials with the driver for iVilotid mobile login.</p>
          <div className="space-y-3 bg-slate-50 rounded-xl p-4 font-mono text-sm">
            <div><span className="text-slate-400 text-xs block">Förarkod</span>{provisionResult.driver_code}</div>
            <div><span className="text-slate-400 text-xs block">Email</span>{provisionResult.email}</div>
            <div><span className="text-slate-400 text-xs block">Password</span>{provisionResult.password}</div>
          </div>
          <button
            onClick={() => {
              navigator.clipboard?.writeText(
                `Förarkod: ${provisionResult.driver_code}\nEmail: ${provisionResult.email}\nPassword: ${provisionResult.password}`,
              );
              showToast('Credentials copied');
            }}
            className="btn-primary w-full mt-4 text-sm"
          >
            Copy credentials
          </button>
          <button onClick={() => setProvisionResult(null)} className="btn-ghost w-full mt-2 text-sm">Close</button>
        </div>
      </div>
    );
  };

  // ── Delete Modal ────────────────────────────────────────────────────────
  const DeleteModal = ({ driver }: { driver: Driver }) => (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-modal w-full max-w-sm animate-slide-up p-6 text-center">
        <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
          <Trash2 className="w-7 h-7 text-red-500" />
        </div>
        <h3 className="font-bold text-slate-800 text-lg mb-1">Delete Driver</h3>
        <p className="text-slate-500 text-sm mb-6">
          Are you sure you want to delete <span className="font-semibold text-slate-700">{driver.full_name}</span>? This cannot be undone.
        </p>
        <div className="flex gap-3 justify-center">
          <button onClick={() => handleDelete(driver)}
            className="px-5 py-2 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-semibold transition">Delete</button>
          <button onClick={() => setDeleteConfirm(null)} className="btn-ghost text-sm">Cancel</button>
        </div>
      </div>
    </div>
  );

  return (
    <Layout title="Drivers" breadcrumbs={[{ label: 'Home', path: '/dashboard' }, { label: 'Drivers' }]}>
      {/* Toolbar */}
      <div className="mb-5 flex flex-col md:flex-row md:justify-between md:items-center gap-3">
        <button onClick={() => setIsAddModalOpen(true)} className="btn-primary w-full md:w-auto">
          <Plus className="w-4 h-4" />Add New Driver
        </button>
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input type="text" placeholder="Search drivers…"
              className="pl-9 pr-4 py-2 w-full md:w-60 text-sm rounded-xl border border-slate-200 bg-white focus:outline-none focus:border-electric-400 focus:ring-2 focus:ring-electric-400/15 transition"
              value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
          </div>
          <div className="relative">
            <select value={filterStatus}
              onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }}
              className="w-full md:w-44 pl-4 pr-9 py-2 text-sm rounded-xl border border-slate-200 bg-white focus:outline-none focus:border-electric-400 appearance-none">
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
            </select>
            <Filter className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          </div>
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2 text-sm text-slate-600">
          <span>Show</span>
          <select value={entriesPerPage} onChange={(e) => { setEntriesPerPage(Number(e.target.value)); setCurrentPage(1); }}
            className="border border-slate-200 rounded-lg px-2 py-1 text-sm bg-white focus:outline-none">
            <option value={10}>10</option><option value={25}>25</option><option value={50}>50</option><option value={100}>100</option>
          </select>
          <span>entries</span>
          <span className="ml-2 text-slate-400">— {total} total</span>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full table-premium">
            <thead>
              <tr>
                {([['Driver', 'full_name'], ['Förarkod', null], ['Contact', 'email'], ['Vehicle', null], ['Status', 'status'], ['Score', 'performance_score'], ['Trips', 'total_trips']] as [string, SortField | null][]).map(([lbl, fld]) => (
                  <th key={lbl} onClick={fld ? () => handleSort(fld) : undefined} className={fld ? 'cursor-pointer select-none' : ''}>
                    <div className="flex items-center gap-1">{lbl}{fld && <SortIcon field={fld} />}</div>
                  </th>
                ))}
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? [...Array(entriesPerPage)].map((_, i) => (
                  <tr key={i}>{[...Array(8)].map((__, j) => <td key={j}><Skeleton className="h-4 w-full" /></td>)}</tr>
                ))
                : drivers.length === 0
                  ? <tr><td colSpan={8} className="text-center text-slate-400 py-10">No drivers found</td></tr>
                  : drivers.map((driver) => (
                    <tr key={driver.id} className="cursor-pointer" onClick={() => handleOpenDriver(driver)}>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className="relative shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-electric-100 to-electric-50 flex items-center justify-center ring-1 ring-slate-100">
                            {driver.status === 'active' && (
                              <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-400 border-2 border-white rounded-full" />
                            )}
                            <span className="text-electric-600 font-bold text-sm">{driver.full_name.charAt(0).toUpperCase()}</span>
                          </div>
                          <div>
                            <div className="font-semibold text-slate-800 text-sm">{driver.full_name}</div>
                            <div className="text-xs text-slate-400">Joined {new Date(driver.created_at).toLocaleDateString()}</div>
                          </div>
                        </div>
                      </td>
                      <td className="font-mono text-sm text-slate-600">
                        {driver.driver_code ?? <span className="text-slate-300 text-xs italic">—</span>}
                      </td>
                      <td>
                        <div className="text-sm text-slate-700">{driver.email ?? '—'}</div>
                        <div className="text-xs text-slate-400">{driver.phone ?? '—'}</div>
                      </td>
                      <td className="text-sm">
                        {driver.vehicle ? `${driver.vehicle.plate}` : <span className="text-slate-300 text-xs italic">Not set</span>}
                      </td>
                      <td>{statusBadge(driver.status)}</td>
                      <td>
                        <div className="flex items-center gap-1">
                          <Star className="h-3.5 w-3.5 text-amber-400 fill-amber-400" />
                          <span className="text-sm font-semibold text-slate-700">{driver.performance_score}%</span>
                        </div>
                      </td>
                      <td className="font-medium text-slate-700">{driver.total_trips}</td>
                      <td>
                        <div className="flex justify-end items-center gap-1" onClick={(e) => e.stopPropagation()}>
                          <button title="Edit" onClick={(e) => { e.stopPropagation(); handleOpenDriver(driver); setEditMode(true); }}
                            className="p-1.5 rounded-lg text-electric-600 hover:bg-electric-50 transition">
                            <Edit className="h-4 w-4" />
                          </button>
                          <button title={driver.status === 'active' ? 'Deactivate' : 'Activate'}
                            onClick={(e) => handleToggleStatus(driver, e)}
                            className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 transition">
                            {driver.status === 'active' ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                          </button>
                          <button title="Delete" onClick={(e) => { e.stopPropagation(); setDeleteConfirm(driver); }}
                            className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 transition">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
              }
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-5 py-4 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 bg-slate-50/60">
          <div className="text-sm text-slate-500">
            Showing{' '}
            <span className="font-medium text-slate-700">{total === 0 ? 0 : (currentPage - 1) * entriesPerPage + 1}</span>
            {' – '}
            <span className="font-medium text-slate-700">{Math.min(currentPage * entriesPerPage, total)}</span>
            {' of '}
            <span className="font-medium text-slate-700">{total}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1}
              className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition">← Prev</button>
            {[...Array(Math.min(5, totalPages))].map((_, i) => {
              const p = i + 1;
              return (
                <button key={i} onClick={() => setCurrentPage(p)}
                  className={`w-8 h-8 text-sm rounded-lg font-medium transition ${currentPage === p ? 'text-white' : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}
                  style={currentPage === p ? { background: 'linear-gradient(135deg,#06B6D4,#0891B2)' } : {}}>
                  {p}
                </button>
              );
            })}
            <button onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages}
              className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition">Next →</button>
          </div>
        </div>
      </div>

      {selectedDriver && (
        <DriverProfileModal
          driver={selectedDriver}
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          editMode={editMode}
          onEditModeChange={setEditMode}
          editForm={editForm}
          setEditForm={setEditForm}
          editAvailableVehicles={editAvailableVehicles}
          onSave={handleSaveEdit}
          onResetPassword={handleResetPassword}
        />
      )}
      <AddDriverModal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        newDriver={newDriver}
        setNewDriver={setNewDriver}
        showCompanyPicker={showCompanyPicker}
        companies={companies}
        availableVehicles={availableVehicles}
        adding={adding}
        onProvision={handleAddDriver}
        onCompanyChange={setAddCompanyId}
      />
      {provisionResult && <CredentialsModal />}
      {deleteConfirm && <DeleteModal driver={deleteConfirm} />}
      {toast && <Toast msg={toast.msg} ok={toast.ok} />}
    </Layout>
  );
};

export default Drivers;
